/**
 * お問合せ自動化: 設定画面。
 * 仕様書: docs/01_spec/spec_inquiry_automation.md C-9.4
 */
import { useState } from 'react';

import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { useInquirySettings, useUpdateInquirySettings } from '@/hooks/useInquiry';

import { InquiryEmbedActions } from './InquiryEmbedActions';

export function InquirySettingsPage() {
  const { data: settings, isLoading } = useInquirySettings();
  const update = useUpdateInquirySettings();

  const [aiAutoEnabled, setAiAutoEnabled] = useState<boolean | null>(null);
  const [threshold, setThreshold] = useState<number | null>(null);
  const [generateDraftWhenOff, setGenerateDraftWhenOff] = useState<boolean | null>(null);
  const [reviewCardEnabled, setReviewCardEnabled] = useState<boolean | null>(null);
  const [reviewCardDelaySeconds, setReviewCardDelaySeconds] = useState<number | null>(null);
  const [piiMaskingEnabled, setPiiMaskingEnabled] = useState<boolean | null>(null);

  // 初回ロード時のみ初期値を設定
  if (settings && aiAutoEnabled === null) {
    setAiAutoEnabled(settings.aiAutoEnabled);
    setThreshold(settings.defaultConfidenceThreshold);
    setGenerateDraftWhenOff(settings.generateDraftWhenOff);
    setReviewCardEnabled(settings.reviewCardEnabled);
    setReviewCardDelaySeconds(settings.reviewCardDelaySeconds);
    setPiiMaskingEnabled(settings.piiMaskingEnabled);
  }

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        aiAutoEnabled: aiAutoEnabled ?? true,
        defaultConfidenceThreshold: threshold ?? 0.85,
        generateDraftWhenOff: generateDraftWhenOff ?? true,
        reviewCardEnabled: reviewCardEnabled ?? true,
        reviewCardDelaySeconds: reviewCardDelaySeconds ?? 2,
        piiMaskingEnabled: piiMaskingEnabled ?? true,
      });
      toast.success('設定を保存しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isLoading || aiAutoEnabled === null) {
    return (
      <Screen title="お問合せ自動化 設定" actions={<InquiryEmbedActions />}>
        <div className="py-8 flex justify-center"><Spinner /></div>
      </Screen>
    );
  }

  return (
    <Screen
      title="お問合せ自動化 設定"
      description="AI 自動応答、信頼度閾値、レビューカード等の動作を制御します"
      actions={<InquiryEmbedActions />}
    >
      <div className="max-w-2xl space-y-6">
        <section className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4">
          <h3 className="text-sm font-semibold mb-3">AI 自動応答</h3>

          <div className="flex items-center justify-between py-2">
            <div>
              <Label>AI 自動応答を有効化</Label>
              <p className="text-xs text-[var(--text-muted)]">
                OFF にすると、すべてのお問合せが事務承認パスに入ります
              </p>
            </div>
            <Switch
              checked={aiAutoEnabled}
              onChange={(e) => setAiAutoEnabled(e.target.checked)}
            />
          </div>

          <div className="py-2">
            <Label>
              デフォルト信頼度閾値（{((threshold ?? 0.85) * 100).toFixed(0)}%）
            </Label>
            <p className="text-xs text-[var(--text-muted)] mb-2">
              AI の信頼度がこの値未満なら事務承認パスに入ります。Phase 2 初期は 85% 推奨。
            </p>
            <input
              type="range"
              min={0.5}
              max={1}
              step={0.05}
              value={threshold ?? 0.85}
              onChange={(e) => setThreshold(parseFloat(e.target.value))}
              className="w-full"
            />
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <Label>OFF 時も AI 下書きを生成</Label>
              <p className="text-xs text-[var(--text-muted)]">
                AI 自動応答が OFF でも、事務向けに AI 下書きを生成します（API コスト発生）
              </p>
            </div>
            <Switch
              checked={generateDraftWhenOff ?? true}
              onChange={(e) => setGenerateDraftWhenOff(e.target.checked)}
            />
          </div>
        </section>

        <section className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4">
          <h3 className="text-sm font-semibold mb-3">レビューカード</h3>

          <div className="flex items-center justify-between py-2">
            <div>
              <Label>レビューカードを表示</Label>
              <p className="text-xs text-[var(--text-muted)]">
                回答後、ユーザーに5段階評価（絵文字）を依頼します
              </p>
            </div>
            <Switch
              checked={reviewCardEnabled ?? true}
              onChange={(e) => setReviewCardEnabled(e.target.checked)}
            />
          </div>

          <div className="py-2">
            <Label>表示までの遅延（秒）</Label>
            <Input
              type="number"
              min={0}
              max={30}
              value={reviewCardDelaySeconds ?? 2}
              onChange={(e) => setReviewCardDelaySeconds(Number(e.target.value))}
              className="w-32"
            />
          </div>
        </section>

        <section className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4">
          <h3 className="text-sm font-semibold mb-3">プライバシー</h3>

          <div className="flex items-center justify-between py-2">
            <div>
              <Label>PII マスキング</Label>
              <p className="text-xs text-[var(--text-muted)]">
                ユーザー質問・AI 回答内のメール・電話番号・郵便番号・カード番号などを保存前に [REDACTED] に置換
              </p>
            </div>
            <Switch
              checked={piiMaskingEnabled ?? true}
              onChange={(e) => setPiiMaskingEnabled(e.target.checked)}
            />
          </div>
        </section>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={update.isPending}>
            保存
          </Button>
        </div>
      </div>
    </Screen>
  );
}
